import React, {FC, PropsWithChildren} from 'react';

import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {makeAnalyticsReportTypeUrl} from '../../../analytics/helpers/analyticsUrlHelpers';
import {WorkspaceGenericItem} from '../../../components/inboxes/workspaces/workspaceGenericItem';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {getUnsavedViewNavigationBlockerId} from '../../../store/panels/analytics/analyticsPanelSelectors';
import {
  getAnalyticsParameters,
  getAnalyticsReportType,
} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {fetchVisibleMetricsForReport} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {AnalyticsReportsEnum} from '../../helpers/analyticsReportHelpers';

/*
 * Props.
 */

interface ReportNavigationLinkProps {
  reportType: AnalyticsReportsEnum;
  iconName: IconName;
  selectedIconName: IconName;
}

/*
 * Component.
 */

export const ReportNavigationLink: FC<PropsWithChildren<ReportNavigationLinkProps>> = ({
  reportType,
  iconName,
  selectedIconName,
  children,
}) => {
  const storeReportType = useAppSelector(getAnalyticsReportType);
  const dispatch = useAppDispatch();

  const doesMatch = reportType === storeReportType;
  const parameters = useAppSelector(getAnalyticsParameters);
  const unsavedViewNavigationBlockerId = useAppSelector(getUnsavedViewNavigationBlockerId);

  const onReportTypeClick = async (clickedReportType: AnalyticsReportsEnum) => {
    // We should ignore blocking navigation for report type and dates changes
    // because they are not stored in views.

    const path = makeAnalyticsReportTypeUrl(clickedReportType, parameters);
    await dispatch(navigateToLinkAction(path, undefined, unsavedViewNavigationBlockerId));
    // Fetch the metric when the report type changes
    dispatch(fetchVisibleMetricsForReport());
  };

  return (
    <WorkspaceGenericItem
      onClick={() => !doesMatch && onReportTypeClick(reportType)}
      isSelected={doesMatch}
      iconName={iconName}
      selectedIconName={selectedIconName}
      shouldAlphabetizeWorkspace={false}
      workspaceDragItem={undefined}
    >
      {children}
    </WorkspaceGenericItem>
  );
};
